import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spin, Typography, Empty, Flex, Tag, Space } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { inventoryOrders } from "@/apis/requirement";
import { OrderV2, OrderItemV2, OrderItemLocation } from '@/types/inventoryV2';
import SText from '@/styles/SText';

import PreviewLocationCode from '@/pages/Warehouse/components/PreviewLocationCode';
import OrderBlock from './OrderBlock';
import ConfirmQuantityModal from './ConfirmQuantityModal';
import { getLocationIdx } from '@/utils/wLocation';
import { Requirement } from '@/types/requirement';
import { OutQtyObj } from '../PreOutOrderPrint';
import { numFormmater } from '@/pages/Requirement/util';
import { itemFetch } from '@/apis/order';
import { shipmentInventory } from '@/apis/shipment';

interface InventoryListProps {
  // REQ：需求计划 ID；SALE：销售明细 ID；SHIPMENT：发货单 ID。
  fetchId: string | string[];
  fetchType: "REQ" | "SALE" | "SHIPMENT";
  number: string;
  onClose: () => void;
  requirement?: Requirement;
  preOutQtyMap?: Record<string, OutQtyObj>;
}

const InventoryList: React.FC<InventoryListProps> = ({ fetchId, fetchType, number, onClose, requirement, preOutQtyMap }) => {
  const { t } = useTranslation();
  const fetchIds = useMemo(() => Array.from(new Set(
    (Array.isArray(fetchId) ? fetchId : [fetchId]).filter(id => id.trim())
  )), [fetchId]);

  const [printing, setPrinting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [details, setDetails] = useState<OrderV2[]>([]);
  const [previewBarcodes, setPreviewBarcodes] = useState<{
    visible: boolean;
    locations: OrderItemLocation[];
    productName: string;
  }>({
    visible: false,
    locations: [],
    productName: ''
  });
  const [confirmQuantityOrderId, setConfirmQuantityOrderId] = useState<string>('');

  const getRequirementItemBySourceId = (sourceId?: string) => {
    if (!sourceId || !requirement?.items?.length) return undefined;
    return requirement.items.find(item => item.id === sourceId);
  };

  useEffect(() => {
    if (fetchIds.length > 0 && fetchType) {
      fetchOrders();
    }
  }, [fetchIds, fetchType]);

  const fetchOrders = () => {
    setLoading(true);
    if (fetchType === "REQ") {
      Promise.all(fetchIds.map(id => inventoryOrders(id)))
        .then(results => {
          const orders = results.flatMap(res => Array.isArray(res?.body) ? res.body : []);
          setDetails(orders.sort((a, b) => b.updatedAt - a.updatedAt));
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (fetchType === "SHIPMENT") {
      Promise.all(fetchIds.map(id => shipmentInventory(id)))
        .then(results => {
          setDetails(results.flatMap(res => Array.isArray(res) ? res : []));
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      Promise.all(fetchIds.map(id => itemFetch(id)))
        .then(results => {
          setDetails(results.flatMap(res => Array.isArray(res?.inventoryOrders) ? res.inventoryOrders : []));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handlePrt = (id: string) => {
    console.log(">>>>",id);
    
    setDetails(last => last.map(order => ({ ...order, printCount: order.id === id ? order.printCount + 1 : order.printCount })))
  };

  // 定义订单项表格列
  const itemColumns: ColumnsType<OrderItemV2> = [
    {
      title: t('purchase.productInfo'),
      children: [
        {
          title: <SText text={t('product.code')} />,
          key: 'productCode',
          width: 120,
          render: (_, record) => <SText text={record.product?.code || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.name')} />,
          key: 'productName',
          width: 150,
          render: (_, record) => <SText text={record.product?.name || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.model')} />,
          key: 'productModel',
          width: 120,
          render: (_, record) => <SText text={record.product?.model || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.uom')} />,
          key: 'uom',
          width: 60,
          align: 'center',
          render: (_, record) => <SText text={record.product?.uom || ''} style={{ width: "100%" }} />
        },
        {
          title: t("product.materialProcess"),
          key: "process",
          width: 120,
          sorter: (a, b) => {
            const valA = a?.product?.process ?? "";
            const valB = b?.product?.process ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, record) => <SText text={record?.product?.process ?? ''} />,
        },
      ]
    },
    {
      title: <SText text={t('warehouse.batchNo')} />,
      dataIndex: 'lot',
      key: 'lot',
      width: 120,
      render: (text) => <SText text={text || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.supplierBatchNo')} />,
      dataIndex: 'thdLot',
      key: 'thdLot',
      width: 120,
      render: (text) => <SText text={text || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.location')} />,
      key: 'locations',
      width: 250,
      render: (_, record) => {
        if (!record.locations || record.locations.length === 0) {
          return '-';
        }
        return <Flex>
          {record.locations.map(loc => (
            <Tag>{getLocationIdx(loc.location)} ({loc.count})</Tag>
          ))}
        </Flex>
      }
    },
    {
      title: <SText text={t('requirement.bomAmount')} />,
      key: 'useAmount',
      width: 90,
      align: 'right',
      render: (_, record) => numFormmater(Number(getRequirementItemBySourceId(record.sourceId)?.bomAmount || '0'))
    },
    {
      title: <SText text={t('requirement.shouldOutCount')} />,
      key: 'shouldOutCount',
      width: 100,
      align: 'right',
      render: (_, record) => numFormmater(Number(getRequirementItemBySourceId(record.sourceId)?.useAmount || '0'))
    },
    {
      title: <SText text={t('requirement.actualOutCount')} />,
      dataIndex: 'count',
      key: 'count',
      width: 80,
      align: 'right',
      fixed: 'right',
      render: (text) => numFormmater(Number(text || '0'))
    },
    {
      title: t('common.opt'),
      key: 'operation',
      width: 120,
      fixed: 'right',
    },
  ];

  const handlePreviewBarcode = (locations: OrderItemLocation[], productName: string) => {
    setPreviewBarcodes({
      visible: true,
      locations,
      productName
    });
  };

  // 打开数量确认弹窗
  const handleOpenConfirmQuantity = (orderId: string) => {
    setConfirmQuantityOrderId(orderId);
  };

  // 数量确认成功回调
  const handleConfirmQuantitySucc = (order: OrderV2) => {
    setDetails(last => last.map(row => order.id === row.id ? order : row));
    setConfirmQuantityOrderId('');
  };

  return (
    <>
      <Modal
        width={1400}
        title={<>
          <Typography.Link>{number}</Typography.Link>
          <span> {t('requirement.outInventory')}</span>
        </>}
        open={fetchIds.length > 0}
        onCancel={onClose}
        footer={null}
        destroyOnClose
      >
        {loading && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}><Spin /></div>}
        {!loading && details.length === 0 && <div style={{ textAlign: 'center', padding: 40 }}><Empty /></div>}
        {!loading && details.length > 0 && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
            {details.map((detail) => (
              <>
                <OrderBlock
                  fetchType={fetchType}
                  key={detail.id}
                  detail={detail}
                  itemColumns={itemColumns}
                  onPreviewBarcode={handlePreviewBarcode}
                  isPrinting={printing}
                  onPrintingChange={setPrinting}
                  loading={loading}
                  preOutSucc={order => {
                    setDetails(last => last.map(row => order.id === row.id ? order : row))
                  }}
                  onOpenConfirmQuantity={handleOpenConfirmQuantity}
                  onConfirmQuantitySucc={handleConfirmQuantitySucc}
                  requirement={requirement}
                  preOutQtyMap={preOutQtyMap}
                  onPrintDtl={handlePrt}
                />
                <hr />
              </>
            ))}
          </div>
        )}
      </Modal>

      {/* 条码预览弹窗 */}
      <PreviewLocationCode
        visible={previewBarcodes.visible}
        onClose={() => setPreviewBarcodes({ visible: false, locations: [], productName: '' })}
        locationBarcodes={previewBarcodes.locations}
        title={`${t('warehouse.viewBarcode')} - ${previewBarcodes.productName}`}
      />

      {/* 数量确认弹窗 */}
      <ConfirmQuantityModal
        orderId={confirmQuantityOrderId}
        onClose={() => setConfirmQuantityOrderId('')}
        onSucc={handleConfirmQuantitySucc}
      />
    </>
  );
};

export default InventoryList;
